import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { InventoryGrid } from '../../components/nursery';
import { Warehouse, RefreshCw } from 'lucide-react';
import nurseryService from '../../services/nursery';
import type { NurseryInventoryRow } from '../../types/nursery';
import { useAppRefresh } from '../../hooks/useAppRefresh';

const STOCK_TYPE_OPTIONS = [
  { label: 'All Types',  value: '' },
  { label: 'Bareroot',   value: 'B' },
  { label: 'Container',  value: 'P' },
];

function InventoryDashboard() {
  const [inventory, setInventory] = useState<NurseryInventoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [stockType, setStockType] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  useAppRefresh(useCallback(() => setRefreshTick(t => t + 1), []));

  const loadInventory = async () => {
    setLoading(true);
    setError(null);
    try {
      // The endpoint returns the whole org summary — filtering is client-side.
      const res = await nurseryService.getInventory();
      if (res.success) {
        setInventory(res.data || []);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, [refreshTick]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await nurseryService.refreshInventory();
      await loadInventory();
    } catch { /* ignore */ }
    setRefreshing(false);
  };

  const visibleInventory = stockType
    ? inventory.filter(r => r.stock_type === stockType)
    : inventory;

  return (
    <>
      <PageHeader
        title="Nursery Inventory"
        subtitle="Stock summary across lots"
        icon={<Warehouse className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Inventory'}
            </Button>
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <select
            value={stockType}
            onChange={e => setStockType(e.target.value)}
            className="border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {STOCK_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-neutral-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : visibleInventory.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Warehouse className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No inventory data</p>
            <p className="text-sm mt-1">Inventory is built from lots. Create some lots first.</p>
          </div>
        ) : (
          <InventoryGrid inventory={visibleInventory} />
        )}
      </div>
    </>
  );
}

export default InventoryDashboard;
